import { gql, useMutation } from '@apollo/client'
import CancelIcon from '@mui/icons-material/Cancel'
import EditIcon from '@mui/icons-material/Edit'
import { Button, Divider, Stack, Typography, useTheme } from '@mui/material'
import Modal from 'components/Modal'
import { useGetMe } from 'components/Session/hooks'
import { VariantType, useSnackbar } from 'notistack'
import React, { useContext, useState } from 'react'
import { Stage } from 'utils/__generated__/graphql'

import { StageForm, StageDataType } from '../../Create'
import { STAGE_FRAGMENT } from '../../constants'
import { DATAROOM_FRAGMENT } from '../../../constants'
import DataroomContext, { DataroomContextType } from '../../context'

const UPDATE_STAGE = gql`
  ${DATAROOM_FRAGMENT}
  ${STAGE_FRAGMENT}
  mutation UPDATE_STAGE($dataroomId: ID!, $stage: StageInput!) {
    updateStage(dataroomId: $dataroomId, stage: $stage) {
      ...DataroomFragment
      stages {
        ...StageFragment
      }
    }
  }
`

function Edit({ stage }: { stage: Stage }) {
  const { dataroom } = useContext<DataroomContextType>(DataroomContext)
  const { data: meData } = useGetMe()

  const [open, setOpen] = useState(false)
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  const handleNotification = (variant: VariantType, message: string) => () => {
    enqueueSnackbar(message, { variant, autoHideDuration: 3000 })
  }

  const onSuccess = handleNotification('success', 'Stage Updated Successfully!')
  const onError = handleNotification('error', 'Something went wrong!')

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const [updateStage] = useMutation(UPDATE_STAGE)

  const handleStage = (data: StageDataType) => {
    updateStage({
      variables: {
        dataroomId: dataroom.id,
        stage: {
          id: stage.id,
          user: meData?.me?.id,
          company: meData?.me?.company?.id,
          ...data,
        },
      },
      onCompleted: () => {
        handleClose()
        onSuccess()
      },
      onError,
    })
  }

  return (
    <React.Fragment>
      <Modal open={open} width="75%" maxWidth={720}>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            sx={{ ...theme.typography.h5, color: theme.palette.text.primary }}
          >
            Edit Stage
          </Typography>
          <CancelIcon
            onClick={handleClose}
            sx={{ cursor: 'pointer', color: theme.palette.error.main }}
          />
        </Stack>
        <Divider sx={{ mt: 1, mb: 1 }} />
        <StageForm
          onStage={handleStage}
          values={{
            name: stage.name || '',
            notes: stage.notes || '',
            timestamp: stage.timestamp ? new Date(stage.timestamp) : null,
          }}
        />
      </Modal>
      <Button
        size="small"
        variant="outlined"
        startIcon={<EditIcon />}
        onClick={handleOpen}
      >
        Edit
      </Button>
    </React.Fragment>
  )
}

export default Edit
